import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaArrowLeft,
  FaArrowDown,
  FaArrowUp,
  FaBullseye,
  FaChartPie,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationCircle,
  FaLayerGroup,
  FaPlus,
  FaSearch,
  FaSpinner,
  FaTimes,
  FaTrashAlt,
  FaWallet,
  FaExchangeAlt,
  FaEdit,
  FaTools,
  FaBalanceScale,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { financeService } from '../../services/apiService';
import VihsMark from '../../components/VihsMark';
import './Finance.css';

const TABS = [
  { id: 'overview', label: 'Visão geral', icon: FaChartPie },
  { id: 'transactions', label: 'Lançamentos', icon: FaExchangeAlt },
  { id: 'accounts', label: 'Contas', icon: FaWallet },
  { id: 'budgets', label: 'Orçamentos', icon: FaLayerGroup },
  { id: 'goals', label: 'Metas', icon: FaBullseye },
  { id: 'categories', label: 'Categorias', icon: FaLayerGroup },
];

const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Conta corrente' },
  { value: 'cash', label: 'Dinheiro' },
  { value: 'savings', label: 'Poupança' },
  { value: 'credit', label: 'Cartão / crédito' },
  { value: 'investment', label: 'Investimento' },
  { value: 'other', label: 'Outra' },
];

const PAYMENT_METHODS = [
  { value: 'pix', label: 'Pix' },
  { value: 'cash', label: 'Dinheiro' },
  { value: 'debit', label: 'Débito' },
  { value: 'credit', label: 'Crédito' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transfer', label: 'Transferência' },
  { value: 'other', label: 'Outro' },
];

const EMPTY_TX = {
  type: 'expense',
  amount: '',
  date: new Date().toISOString().slice(0, 10),
  description: '',
  notes: '',
  accountId: '',
  categoryId: '',
  transferToAccountId: '',
  paymentMethod: 'pix',
  status: 'confirmed',
  tags: '',
};

const EMPTY_ACCOUNT = {
  name: '',
  type: 'checking',
  initialBalance: '0',
  color: '#80c2e8',
  notes: '',
};

const EMPTY_CATEGORY = {
  name: '',
  type: 'expense',
  color: '#d4a35c',
};

const EMPTY_BUDGET = {
  categoryId: '',
  limitAmount: '',
  notes: '',
};

const EMPTY_GOAL = {
  name: '',
  targetAmount: '',
  currentAmount: '0',
  deadline: '',
  color: '#4dbc8b',
  notes: '',
  status: 'active',
};

function money(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0);
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(value));
  } catch {
    return '—';
  }
}

function monthLabel(monthKey) {
  const [year, month] = String(monthKey || '').split('-').map(Number);
  if (!year || !month) return monthKey;
  try {
    return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' })
      .format(new Date(year, month - 1, 1));
  } catch {
    return monthKey;
  }
}

function shiftMonth(monthKey, delta) {
  const [year, month] = String(monthKey).split('-').map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function parseAmountInput(value) {
  if (typeof value === 'number') return value;
  const normalized = String(value || '')
    .trim()
    .replace(/\s/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : NaN;
}

function CashflowChart({ data }) {
  const width = 640;
  const height = 200;
  const pad = { top: 18, right: 8, bottom: 26, left: 8 };
  const max = Math.max(1, ...data.flatMap((item) => [item.income || 0, item.expense || 0]));
  const hasData = data.some((item) => (item.income || 0) > 0 || (item.expense || 0) > 0);
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const groupW = innerW / Math.max(data.length, 1);
  const barW = Math.max(9, groupW * 0.26);

  if (!hasData) {
    return (
      <div className="finance-empty" style={{ minHeight: 160, width: '100%', border: 0, background: 'transparent' }}>
        <strong>Sem movimentação ainda</strong>
        <span>Os gráficos aparecem assim que houver receitas ou despesas.</span>
      </div>
    );
  }

  return (
    <svg className="finance-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Fluxo de caixa dos últimos meses">
      <line
        x1={pad.left}
        x2={width - pad.right}
        y1={pad.top + innerH}
        y2={pad.top + innerH}
        stroke="rgba(126,182,217,0.12)"
        strokeWidth="1"
      />
      {data.map((item, index) => {
        const x = pad.left + index * groupW + groupW / 2;
        const incomeH = ((item.income || 0) / max) * innerH;
        const expenseH = ((item.expense || 0) / max) * innerH;
        const label = String(item.month || '').slice(5);
        return (
          <g key={item.month}>
            <rect
              x={x - barW - 2}
              y={pad.top + innerH - Math.max(incomeH, incomeH > 0 ? 3 : 0)}
              width={barW}
              height={Math.max(incomeH, incomeH > 0 ? 3 : 0)}
              rx="3"
              fill="#4dbc8b"
              opacity="0.92"
            />
            <rect
              x={x + 2}
              y={pad.top + innerH - Math.max(expenseH, expenseH > 0 ? 3 : 0)}
              width={barW}
              height={Math.max(expenseH, expenseH > 0 ? 3 : 0)}
              rx="3"
              fill="#c45a5a"
              opacity="0.9"
            />
            <text
              x={x}
              y={height - 8}
              textAnchor="middle"
              fill="rgba(214,226,236,0.42)"
              fontSize="11"
              fontFamily="Manrope, sans-serif"
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function Finance() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState('overview');
  const [month, setMonth] = useState(currentMonthKey());
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState('');
  const [error, setError] = useState('');
  const [flash, setFlash] = useState(null);

  const [overview, setOverview] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 30, total: 0, pages: 1 });
  const [budgets, setBudgets] = useState([]);
  const [goals, setGoals] = useState([]);

  const [txFilters, setTxFilters] = useState({
    q: '',
    type: '',
    accountId: '',
    categoryId: '',
    page: 1,
  });
  const [searchInput, setSearchInput] = useState('');

  const [modal, setModal] = useState(null);
  const [txForm, setTxForm] = useState(EMPTY_TX);
  const [accountForm, setAccountForm] = useState(EMPTY_ACCOUNT);
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY);
  const [budgetForm, setBudgetForm] = useState(EMPTY_BUDGET);
  const [goalForm, setGoalForm] = useState(EMPTY_GOAL);
  const [editingId, setEditingId] = useState(null);

  const showFlash = useCallback((type, message) => {
    setFlash({ type, message });
    window.setTimeout(() => setFlash(null), 3200);
  }, []);

  const incomeCategories = useMemo(
    () => categories.filter((item) => item.type === 'income' && item.active !== false),
    [categories]
  );
  const expenseCategories = useMemo(
    () => categories.filter((item) => item.type === 'expense' && item.active !== false),
    [categories]
  );

  const loadCore = useCallback(async () => {
    const [accountsRes, categoriesRes] = await Promise.all([
      financeService.listAccounts(),
      financeService.listCategories(),
    ]);
    setAccounts(accountsRes?.data?.accounts || []);
    setCategories(categoriesRes?.data?.categories || []);
  }, []);

  const loadOverview = useCallback(async () => {
    const response = await financeService.getOverview({ month });
    setOverview(response?.data?.overview || null);
  }, [month]);

  const loadTransactions = useCallback(async () => {
    const response = await financeService.listTransactions({
      q: txFilters.q || undefined,
      type: txFilters.type || undefined,
      accountId: txFilters.accountId || undefined,
      categoryId: txFilters.categoryId || undefined,
      from: `${month}-01`,
      to: `${month}-31`,
      page: txFilters.page || 1,
      limit: 30,
    });
    setTransactions(response?.data?.transactions || []);
    setPagination(response?.data?.pagination || { page: 1, limit: 30, total: 0, pages: 1 });
  }, [month, txFilters]);

  const loadBudgets = useCallback(async () => {
    const response = await financeService.listBudgets({ month });
    setBudgets(response?.data?.budgets || []);
  }, [month]);

  const loadGoals = useCallback(async () => {
    const response = await financeService.listGoals();
    setGoals(response?.data?.goals || []);
  }, []);

  const bootstrapAndLoad = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await financeService.bootstrap();
      await loadCore();
      if (tab === 'overview') await loadOverview();
      if (tab === 'transactions') await loadTransactions();
      if (tab === 'budgets') await loadBudgets();
      if (tab === 'goals' || tab === 'overview') await loadGoals();
      if (tab === 'accounts') await loadCore();
      if (tab === 'categories') await loadCore();
    } catch (err) {
      setError(err.message || 'Não foi possível carregar o ambiente financeiro.');
    } finally {
      setLoading(false);
    }
  }, [tab, loadCore, loadOverview, loadTransactions, loadBudgets, loadGoals]);

  useEffect(() => {
    bootstrapAndLoad();
  }, [bootstrapAndLoad]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setTxFilters((current) => ({ ...current, q: searchInput, page: 1 }));
    }, 280);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const refreshAfterMutation = useCallback(async () => {
    await loadCore();
    if (tab === 'overview') {
      await Promise.all([loadOverview(), loadGoals()]);
    }
    if (tab === 'transactions') await loadTransactions();
    if (tab === 'budgets') await loadBudgets();
    if (tab === 'goals') await loadGoals();
  }, [tab, loadCore, loadOverview, loadTransactions, loadBudgets, loadGoals]);

  const openTxModal = (transaction = null) => {
    if (transaction) {
      setEditingId(transaction.id);
      setTxForm({
        type: transaction.type,
        amount: String(transaction.amount).replace('.', ','),
        date: String(transaction.date || '').slice(0, 10),
        description: transaction.description || '',
        notes: transaction.notes || '',
        accountId: transaction.accountId || '',
        categoryId: transaction.categoryId || '',
        transferToAccountId: transaction.transferToAccountId || '',
        paymentMethod: transaction.paymentMethod || 'pix',
        status: transaction.status || 'confirmed',
        tags: (transaction.tags || []).join(', '),
      });
    } else {
      setEditingId(null);
      setTxForm({
        ...EMPTY_TX,
        accountId: accounts[0]?.id || '',
        categoryId: expenseCategories[0]?.id || '',
      });
    }
    setModal('transaction');
  };

  const openAccountModal = (account = null) => {
    if (account) {
      setEditingId(account.id);
      setAccountForm({
        name: account.name || '',
        type: account.type || 'checking',
        initialBalance: String(account.initialBalance ?? 0).replace('.', ','),
        color: account.color || '#80c2e8',
        notes: account.notes || '',
      });
    } else {
      setEditingId(null);
      setAccountForm(EMPTY_ACCOUNT);
    }
    setModal('account');
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingId(category.id);
      setCategoryForm({
        name: category.name || '',
        type: category.type || 'expense',
        color: category.color || '#d4a35c',
      });
    } else {
      setEditingId(null);
      setCategoryForm(EMPTY_CATEGORY);
    }
    setModal('category');
  };

  const openBudgetModal = (budget = null) => {
    if (budget) {
      setEditingId(budget.id);
      setBudgetForm({
        categoryId: budget.categoryId || '',
        limitAmount: String(budget.limitAmount || '').replace('.', ','),
        notes: budget.notes || '',
      });
    } else {
      setEditingId(null);
      setBudgetForm({
        ...EMPTY_BUDGET,
        categoryId: expenseCategories[0]?.id || '',
      });
    }
    setModal('budget');
  };

  const openGoalModal = (goal = null) => {
    if (goal) {
      setEditingId(goal.id);
      setGoalForm({
        name: goal.name || '',
        targetAmount: String(goal.targetAmount || '').replace('.', ','),
        currentAmount: String(goal.currentAmount || 0).replace('.', ','),
        deadline: goal.deadline ? String(goal.deadline).slice(0, 10) : '',
        color: goal.color || '#4dbc8b',
        notes: goal.notes || '',
        status: goal.status || 'active',
      });
    } else {
      setEditingId(null);
      setGoalForm(EMPTY_GOAL);
    }
    setModal('goal');
  };

  const closeModal = () => {
    setModal(null);
    setEditingId(null);
  };

  const saveTransaction = async (event) => {
    event.preventDefault();
    setActing('tx');
    try {
      const amount = parseAmountInput(txForm.amount);
      if (!(amount > 0)) throw new Error('Informe um valor válido.');
      const payload = {
        type: txForm.type,
        amount,
        date: txForm.date,
        description: txForm.description.trim(),
        notes: txForm.notes.trim(),
        accountId: txForm.accountId,
        categoryId: txForm.type === 'transfer' ? undefined : txForm.categoryId,
        transferToAccountId: txForm.type === 'transfer' ? txForm.transferToAccountId : undefined,
        paymentMethod: txForm.paymentMethod,
        status: txForm.status,
        tags: txForm.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      };
      if (editingId) await financeService.updateTransaction(editingId, payload);
      else await financeService.createTransaction(payload);
      showFlash('ok', editingId ? 'Lançamento atualizado.' : 'Lançamento registrado.');
      closeModal();
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao salvar lançamento.');
    } finally {
      setActing('');
    }
  };

  const saveAccount = async (event) => {
    event.preventDefault();
    setActing('account');
    try {
      const payload = {
        name: accountForm.name.trim(),
        type: accountForm.type,
        initialBalance: parseAmountInput(accountForm.initialBalance) || 0,
        color: accountForm.color,
        notes: accountForm.notes.trim(),
      };
      if (editingId) await financeService.updateAccount(editingId, payload);
      else await financeService.createAccount(payload);
      showFlash('ok', editingId ? 'Conta atualizada.' : 'Conta criada.');
      closeModal();
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao salvar conta.');
    } finally {
      setActing('');
    }
  };

  const saveCategory = async (event) => {
    event.preventDefault();
    setActing('category');
    try {
      const payload = {
        name: categoryForm.name.trim(),
        type: categoryForm.type,
        color: categoryForm.color,
      };
      if (editingId) await financeService.updateCategory(editingId, payload);
      else await financeService.createCategory(payload);
      showFlash('ok', editingId ? 'Categoria atualizada.' : 'Categoria criada.');
      closeModal();
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao salvar categoria.');
    } finally {
      setActing('');
    }
  };

  const saveBudget = async (event) => {
    event.preventDefault();
    setActing('budget');
    try {
      const limitAmount = parseAmountInput(budgetForm.limitAmount);
      if (!(limitAmount > 0)) throw new Error('Informe um limite válido.');
      await financeService.upsertBudget({
        month,
        categoryId: budgetForm.categoryId,
        limitAmount,
        notes: budgetForm.notes.trim(),
      });
      showFlash('ok', 'Orçamento salvo.');
      closeModal();
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao salvar orçamento.');
    } finally {
      setActing('');
    }
  };

  const saveGoal = async (event) => {
    event.preventDefault();
    setActing('goal');
    try {
      const payload = {
        name: goalForm.name.trim(),
        targetAmount: parseAmountInput(goalForm.targetAmount),
        currentAmount: parseAmountInput(goalForm.currentAmount) || 0,
        deadline: goalForm.deadline || null,
        color: goalForm.color,
        notes: goalForm.notes.trim(),
        status: goalForm.status,
      };
      if (!(payload.targetAmount > 0)) throw new Error('Informe um valor alvo válido.');
      if (editingId) await financeService.updateGoal(editingId, payload);
      else await financeService.createGoal(payload);
      showFlash('ok', editingId ? 'Meta atualizada.' : 'Meta criada.');
      closeModal();
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao salvar meta.');
    } finally {
      setActing('');
    }
  };

  const removeTransaction = async (id) => {
    if (!window.confirm('Remover este lançamento?')) return;
    setActing(`del-tx-${id}`);
    try {
      await financeService.deleteTransaction(id);
      showFlash('ok', 'Lançamento removido.');
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao remover.');
    } finally {
      setActing('');
    }
  };

  const removeAccount = async (id) => {
    if (!window.confirm('Arquivar ou remover esta conta?')) return;
    setActing(`del-acc-${id}`);
    try {
      const response = await financeService.deleteAccount(id);
      showFlash('ok', response?.message || 'Conta removida.');
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao remover conta.');
    } finally {
      setActing('');
    }
  };

  const removeCategory = async (id) => {
    if (!window.confirm('Arquivar ou remover esta categoria?')) return;
    setActing(`del-cat-${id}`);
    try {
      const response = await financeService.deleteCategory(id);
      showFlash('ok', response?.message || 'Categoria removida.');
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao remover categoria.');
    } finally {
      setActing('');
    }
  };

  const removeBudget = async (id) => {
    if (!window.confirm('Remover este orçamento?')) return;
    setActing(`del-bud-${id}`);
    try {
      await financeService.deleteBudget(id);
      showFlash('ok', 'Orçamento removido.');
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao remover orçamento.');
    } finally {
      setActing('');
    }
  };

  const removeGoal = async (id) => {
    if (!window.confirm('Remover esta meta?')) return;
    setActing(`del-goal-${id}`);
    try {
      await financeService.deleteGoal(id);
      showFlash('ok', 'Meta removida.');
      await refreshAfterMutation();
    } catch (err) {
      showFlash('danger', err.message || 'Falha ao remover meta.');
    } finally {
      setActing('');
    }
  };

  const summary = overview?.summary || {};
  const categoryOptions = txForm.type === 'income' ? incomeCategories : expenseCategories;
  const hasActivity = (summary.transactionCount || 0) > 0
    || (overview?.recentTransactions || []).length > 0
    || (summary.income || 0) > 0
    || (summary.expense || 0) > 0;

  return (
    <main className="finance-page">
      <header className="finance-topbar">
        <Link to="/" className="finance-brand" aria-label="Vih's — página inicial">
          <VihsMark />
          Vih&apos;s
        </Link>
        <div className="finance-topbar-actions">
          {me?.role === 'admin' && (
            <Link to="/admin" className="finance-ghost-link">
              <FaTools aria-hidden="true" />
              <span>Painel admin</span>
            </Link>
          )}
          <Link to="/conta" className="finance-back">
            <FaArrowLeft aria-hidden="true" />
            Conta
          </Link>
        </div>
      </header>

      <div className="finance-shell">
        <motion.div
          className="finance-hero"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <div>
            <p className="finance-eyebrow">Privado · Dono</p>
            <h1>Finanças</h1>
            <p className="finance-lead">
              Painel pessoal para saldo, gastos, orçamentos e metas.
            </p>
          </div>
          <div className="finance-hero-actions">
            <div className="finance-hero-row">
              <span className="finance-badge">Acesso restrito</span>
              <p className="finance-user-tag">@{me?.username}</p>
            </div>
            <div className="finance-hero-row">
              <div className="finance-month-picker">
                <button type="button" aria-label="Mês anterior" onClick={() => setMonth((m) => shiftMonth(m, -1))}>
                  <FaChevronLeft />
                </button>
                <span>{monthLabel(month)}</span>
                <button type="button" aria-label="Próximo mês" onClick={() => setMonth((m) => shiftMonth(m, 1))}>
                  <FaChevronRight />
                </button>
              </div>
              <button type="button" className="finance-btn finance-btn-primary" onClick={() => openTxModal()}>
                <FaPlus /> Novo lançamento
              </button>
            </div>
          </div>
        </motion.div>

        <nav className="finance-tabs" aria-label="Seções financeiras">
          {TABS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={tab === item.id ? 'is-active' : ''}
                onClick={() => setTab(item.id)}
              >
                <Icon aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <AnimatePresence mode="wait">
          {flash && (
            <motion.div
              key={flash.message}
              className={`finance-flash is-${flash.type}`}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {flash.type === 'ok' ? <FaCheckCircle /> : <FaExclamationCircle />}
              {flash.message}
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <div className="finance-error">
            <FaExclamationCircle />
            {error}
          </div>
        )}

        {loading ? (
          <div className="finance-loading">
            <FaSpinner />
            Carregando ambiente financeiro…
          </div>
        ) : (
          <>
            {tab === 'overview' && (
              <motion.div
                className="finance-stack"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
              >
                <div className="finance-kpi-grid">
                  <article className="finance-kpi is-accent">
                    <span className="finance-kpi-label"><FaWallet /> Patrimônio</span>
                    <strong>{money(summary.balance)}</strong>
                    <small>{summary.accountsCount || 0} conta(s) ativa(s)</small>
                  </article>
                  <article className="finance-kpi is-ok">
                    <span className="finance-kpi-label"><FaArrowUp /> Receitas</span>
                    <strong>{money(summary.income)}</strong>
                    <small>
                      {summary.incomeChange == null
                        ? 'Sem comparação ainda'
                        : `${summary.incomeChange > 0 ? '+' : ''}${summary.incomeChange}% vs mês anterior`}
                    </small>
                  </article>
                  <article className="finance-kpi is-danger">
                    <span className="finance-kpi-label"><FaArrowDown /> Despesas</span>
                    <strong>{money(summary.expense)}</strong>
                    <small>
                      {summary.expenseChange == null
                        ? 'Sem comparação ainda'
                        : `${summary.expenseChange > 0 ? '+' : ''}${summary.expenseChange}% vs mês anterior`}
                    </small>
                  </article>
                  <article className={`finance-kpi ${summary.net >= 0 ? 'is-ok' : 'is-danger'}`}>
                    <span className="finance-kpi-label"><FaBalanceScale /> Resultado</span>
                    <strong>{money(summary.net)}</strong>
                    <small>Poupança: {summary.savingsRate || 0}%</small>
                  </article>
                </div>

                {!hasActivity && (
                  <div className="finance-welcome">
                    <div>
                      <span className="finance-card-eyebrow">Primeiros passos</span>
                      <h2>Comece registrando um lançamento</h2>
                      <p>
                        Crie uma receita ou despesa para ativar gráficos, distribuição por categoria
                        e o histórico recente. Contas e categorias padrão já estão prontas.
                      </p>
                    </div>
                    <div className="finance-welcome-actions">
                      <button type="button" className="finance-btn finance-btn-ghost" onClick={() => setTab('categories')}>
                        Ver categorias
                      </button>
                      <button type="button" className="finance-btn finance-btn-primary" onClick={() => openTxModal()}>
                        <FaPlus /> Novo lançamento
                      </button>
                    </div>
                  </div>
                )}

                {hasActivity ? (
                  <>
                    <div className="finance-grid-2">
                      <article className="finance-card">
                        <div className="finance-card-head">
                          <div>
                            <span className="finance-card-eyebrow">Fluxo</span>
                            <h2>Últimos 6 meses</h2>
                          </div>
                        </div>
                        <div className="finance-card-body">
                          <div className="finance-chart-wrap">
                            <CashflowChart data={overview?.cashflow || []} />
                          </div>
                          <div className="finance-chart-legend">
                            <span><i style={{ background: '#4dbc8b' }} /> Receitas</span>
                            <span><i style={{ background: '#c45a5a' }} /> Despesas</span>
                          </div>
                        </div>
                      </article>

                      <article className="finance-card">
                        <div className="finance-card-head">
                          <div>
                            <span className="finance-card-eyebrow">Distribuição</span>
                            <h2>Gastos por categoria</h2>
                          </div>
                        </div>
                        <div className="finance-card-body">
                          {(overview?.topCategories || []).length === 0 ? (
                            <div className="finance-empty">
                              <strong>Sem despesas neste mês</strong>
                              <span>Os gastos aparecem aqui agrupados por categoria.</span>
                            </div>
                          ) : (
                            <div className="finance-bars">
                              {(overview?.topCategories || []).map((item) => (
                                <div key={item.categoryId} className="finance-bar-row">
                                  <div className="finance-bar-meta">
                                    <strong>{item.name}</strong>
                                    <span>{money(item.total)} · {item.share}%</span>
                                  </div>
                                  <div className="finance-bar-track">
                                    <div
                                      className="finance-bar-fill"
                                      style={{
                                        width: `${Math.min(100, item.share)}%`,
                                        background: item.color,
                                      }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </article>
                    </div>

                    <div className="finance-grid-3">
                      <article className="finance-card">
                        <div className="finance-card-head">
                          <div>
                            <span className="finance-card-eyebrow">Contas</span>
                            <h3>Saldos</h3>
                          </div>
                          <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => setTab('accounts')}>
                            Ver tudo
                          </button>
                        </div>
                        <div className="finance-card-body">
                          <div className="finance-list">
                            {(overview?.accounts || []).slice(0, 5).map((account) => (
                              <div key={account.id} className="finance-list-item">
                                <span className="finance-dot" style={{ background: account.color }} />
                                <div>
                                  <strong>{account.name}</strong>
                                  <small>{ACCOUNT_TYPES.find((t) => t.value === account.type)?.label || account.type}</small>
                                </div>
                                <span className="finance-amount">{money(account.balance)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </article>

                      <article className="finance-card">
                        <div className="finance-card-head">
                          <div>
                            <span className="finance-card-eyebrow">Orçamento</span>
                            <h3>Limites do mês</h3>
                          </div>
                          <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => setTab('budgets')}>
                            Ajustar
                          </button>
                        </div>
                        <div className="finance-card-body">
                          {(overview?.budgets || []).length === 0 ? (
                            <div className="finance-empty">
                              <span>Nenhum limite definido para este mês.</span>
                            </div>
                          ) : (
                            <div className="finance-list">
                              {(overview?.budgets || []).slice(0, 5).map((budget) => (
                                <div key={budget.id} className="finance-list-item">
                                  <span className="finance-dot" style={{ background: budget.category?.color || '#80c2e8' }} />
                                  <div>
                                    <strong>{budget.category?.name || 'Categoria'}</strong>
                                    <small>{money(budget.spent)} de {money(budget.limitAmount)}</small>
                                  </div>
                                  <span className={`finance-chip ${budget.overBudget ? 'is-danger' : budget.usage > 80 ? 'is-warn' : 'is-ok'}`}>
                                    {budget.usage}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </article>

                      <article className="finance-card">
                        <div className="finance-card-head">
                          <div>
                            <span className="finance-card-eyebrow">Recente</span>
                            <h3>Últimos lançamentos</h3>
                          </div>
                          <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => setTab('transactions')}>
                            Ver tudo
                          </button>
                        </div>
                        <div className="finance-card-body">
                          <div className="finance-list">
                            {(overview?.recentTransactions || []).map((tx) => (
                              <div key={tx.id} className="finance-list-item">
                                <span className="finance-dot" style={{ background: tx.category?.color || tx.account?.color || '#80c2e8' }} />
                                <div>
                                  <strong>{tx.description}</strong>
                                  <small>{formatDate(tx.date)} · {tx.category?.name || tx.type}</small>
                                </div>
                                <span className={`finance-amount is-${tx.type}`}>
                                  {tx.type === 'expense' ? '−' : tx.type === 'income' ? '+' : ''}
                                  {money(tx.amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </article>
                    </div>
                  </>
                ) : (
                  <div className="finance-grid-2">
                    <article className="finance-card finance-card-compact">
                      <div className="finance-card-head">
                        <div>
                          <span className="finance-card-eyebrow">Contas</span>
                          <h2>Saldos prontos</h2>
                        </div>
                        <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => setTab('accounts')}>
                          Gerenciar
                        </button>
                      </div>
                      <div className="finance-card-body">
                        <div className="finance-list">
                          {(overview?.accounts || accounts).slice(0, 4).map((account) => (
                            <div key={account.id} className="finance-list-item">
                              <span className="finance-dot" style={{ background: account.color }} />
                              <div>
                                <strong>{account.name}</strong>
                                <small>{ACCOUNT_TYPES.find((t) => t.value === account.type)?.label || account.type}</small>
                              </div>
                              <span className="finance-amount">{money(account.balance)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </article>

                    <article className="finance-card finance-card-compact">
                      <div className="finance-card-head">
                        <div>
                          <span className="finance-card-eyebrow">Categorias</span>
                          <h2>Organização padrão</h2>
                        </div>
                        <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => setTab('categories')}>
                          Ver tudo
                        </button>
                      </div>
                      <div className="finance-card-body">
                        <div className="finance-list">
                          {categories.slice(0, 5).map((category) => (
                            <div key={category.id} className="finance-list-item">
                              <span className="finance-dot" style={{ background: category.color }} />
                              <div>
                                <strong>{category.name}</strong>
                                <small>{category.type === 'income' ? 'Receita' : 'Despesa'}</small>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </article>
                  </div>
                )}
              </motion.div>
            )}

            {tab === 'transactions' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="finance-toolbar">
                  <div className="finance-search">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="Buscar descrição, notas ou tags…"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                  <select
                    value={txFilters.type}
                    onChange={(e) => setTxFilters((c) => ({ ...c, type: e.target.value, page: 1 }))}
                  >
                    <option value="">Todos os tipos</option>
                    <option value="income">Receitas</option>
                    <option value="expense">Despesas</option>
                    <option value="transfer">Transferências</option>
                  </select>
                  <select
                    value={txFilters.accountId}
                    onChange={(e) => setTxFilters((c) => ({ ...c, accountId: e.target.value, page: 1 }))}
                  >
                    <option value="">Todas as contas</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>{account.name}</option>
                    ))}
                  </select>
                  <select
                    value={txFilters.categoryId}
                    onChange={(e) => setTxFilters((c) => ({ ...c, categoryId: e.target.value, page: 1 }))}
                  >
                    <option value="">Todas as categorias</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                  <button type="button" className="finance-btn finance-btn-primary" onClick={() => openTxModal()}>
                    <FaPlus /> Novo lançamento
                  </button>
                </div>

                <div className="finance-table-wrap">
                  <table className="finance-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Descrição</th>
                        <th>Categoria / Conta</th>
                        <th>Valor</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id}>
                          <td>{formatDate(tx.date)}</td>
                          <td>
                            <strong>{tx.description}</strong>
                            <small>
                              {tx.type === 'income' ? 'Receita' : tx.type === 'expense' ? 'Despesa' : 'Transferência'}
                              {tx.paymentMethod ? ` · ${PAYMENT_METHODS.find((p) => p.value === tx.paymentMethod)?.label || tx.paymentMethod}` : ''}
                            </small>
                          </td>
                          <td>
                            <strong>{tx.category?.name || (tx.type === 'transfer' ? 'Entre contas' : '—')}</strong>
                            <small>
                              {tx.account?.name || '—'}
                              {tx.transferToAccount ? ` → ${tx.transferToAccount.name}` : ''}
                            </small>
                          </td>
                          <td>
                            <span className={`finance-amount is-${tx.type}`}>
                              {tx.type === 'expense' ? '−' : tx.type === 'income' ? '+' : ''}
                              {money(tx.amount)}
                            </span>
                          </td>
                          <td>
                            <div className="finance-row-actions">
                              <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => openTxModal(tx)}>
                                <FaEdit />
                              </button>
                              <button
                                type="button"
                                className="finance-btn finance-btn-danger finance-btn-sm"
                                disabled={acting === `del-tx-${tx.id}`}
                                onClick={() => removeTransaction(tx.id)}
                              >
                                <FaTrashAlt />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {transactions.length === 0 && (
                    <div className="finance-empty">
                      <strong>Nenhum lançamento neste filtro</strong>
                      <span>Ajuste os filtros ou registre a primeira movimentação.</span>
                    </div>
                  )}
                </div>

                <div className="finance-pagination">
                  <span>
                    Página {pagination.page} de {pagination.pages} · {pagination.total} lançamento(s)
                  </span>
                  <div className="finance-row-actions">
                    <button
                      type="button"
                      className="finance-btn finance-btn-ghost finance-btn-sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setTxFilters((c) => ({ ...c, page: Math.max(1, c.page - 1) }))}
                    >
                      Anterior
                    </button>
                    <button
                      type="button"
                      className="finance-btn finance-btn-ghost finance-btn-sm"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => setTxFilters((c) => ({ ...c, page: c.page + 1 }))}
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {tab === 'accounts' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="finance-toolbar">
                  <div />
                  <button type="button" className="finance-btn finance-btn-primary" onClick={() => openAccountModal()}>
                    <FaPlus /> Nova conta
                  </button>
                </div>
                <div className="finance-accounts-grid">
                  {accounts.map((account) => (
                    <article key={account.id} className="finance-account-card">
                      <header>
                        <div>
                          <h3>{account.name}</h3>
                          <p>{ACCOUNT_TYPES.find((t) => t.value === account.type)?.label || account.type}</p>
                        </div>
                        <span className="finance-dot" style={{ width: 14, height: 14, background: account.color }} />
                      </header>
                      <div className="finance-account-balance">{money(account.balance)}</div>
                      <p>Saldo inicial: {money(account.initialBalance)}</p>
                      <div className="finance-row-actions">
                        <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => openAccountModal(account)}>
                          <FaEdit /> Editar
                        </button>
                        <button
                          type="button"
                          className="finance-btn finance-btn-danger finance-btn-sm"
                          disabled={acting === `del-acc-${account.id}`}
                          onClick={() => removeAccount(account.id)}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </motion.div>
            )}

            {tab === 'budgets' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="finance-toolbar">
                  <p style={{ margin: 0, color: 'var(--muted)', alignSelf: 'center' }}>
                    Orçamentos de <strong style={{ color: 'var(--ink)' }}>{monthLabel(month)}</strong>
                  </p>
                  <button type="button" className="finance-btn finance-btn-primary" onClick={() => openBudgetModal()}>
                    <FaPlus /> Definir limite
                  </button>
                </div>
                <div className="finance-goals-grid">
                  {budgets.map((budget) => (
                    <article key={budget.id} className="finance-goal-card">
                      <header>
                        <div>
                          <h3>{budget.category?.name || 'Categoria'}</h3>
                          <p>{money(budget.spent)} gastos · {money(budget.remaining)} restantes</p>
                        </div>
                        <span className={`finance-chip ${budget.overBudget ? 'is-danger' : budget.usage > 80 ? 'is-warn' : 'is-ok'}`}>
                          {budget.usage}%
                        </span>
                      </header>
                      <div className="finance-progress">
                        <span
                          style={{
                            width: `${Math.min(100, budget.usage)}%`,
                            background: budget.overBudget ? 'var(--danger)' : budget.category?.color || 'var(--accent)',
                          }}
                        />
                      </div>
                      <strong>{money(budget.limitAmount)} limite</strong>
                      <div className="finance-row-actions">
                        <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => openBudgetModal(budget)}>
                          <FaEdit /> Ajustar
                        </button>
                        <button
                          type="button"
                          className="finance-btn finance-btn-danger finance-btn-sm"
                          disabled={acting === `del-bud-${budget.id}`}
                          onClick={() => removeBudget(budget.id)}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </article>
                  ))}
                  {budgets.length === 0 && (
                    <div className="finance-empty" style={{ gridColumn: '1 / -1' }}>
                      <strong>Nenhum orçamento neste mês</strong>
                      <span>Defina limites por categoria para acompanhar o ritmo de gastos.</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {tab === 'goals' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="finance-toolbar">
                  <div />
                  <button type="button" className="finance-btn finance-btn-primary" onClick={() => openGoalModal()}>
                    <FaPlus /> Nova meta
                  </button>
                </div>
                <div className="finance-goals-grid">
                  {goals.map((goal) => (
                    <article key={goal.id} className="finance-goal-card">
                      <header>
                        <div>
                          <h3>{goal.name}</h3>
                          <p>
                            {money(goal.currentAmount)} de {money(goal.targetAmount)}
                            {goal.deadline ? ` · até ${formatDate(goal.deadline)}` : ''}
                          </p>
                        </div>
                        <span className={`finance-chip ${goal.status === 'completed' ? 'is-ok' : goal.status === 'paused' ? 'is-warn' : ''}`}>
                          {goal.status === 'completed' ? 'Concluída' : goal.status === 'paused' ? 'Pausada' : 'Ativa'}
                        </span>
                      </header>
                      <div className="finance-progress">
                        <span style={{ width: `${Math.min(100, goal.progress)}%`, background: goal.color }} />
                      </div>
                      <strong>{goal.progress}% · falta {money(goal.remaining)}</strong>
                      <div className="finance-row-actions">
                        <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => openGoalModal(goal)}>
                          <FaEdit /> Editar
                        </button>
                        <button
                          type="button"
                          className="finance-btn finance-btn-danger finance-btn-sm"
                          disabled={acting === `del-goal-${goal.id}`}
                          onClick={() => removeGoal(goal.id)}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </article>
                  ))}
                  {goals.length === 0 && (
                    <div className="finance-empty" style={{ gridColumn: '1 / -1' }}>
                      <strong>Nenhuma meta cadastrada</strong>
                      <span>Crie objetivos como reserva de emergência, equipamento ou viagem.</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {tab === 'categories' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="finance-toolbar">
                  <div />
                  <button type="button" className="finance-btn finance-btn-primary" onClick={() => openCategoryModal()}>
                    <FaPlus /> Nova categoria
                  </button>
                </div>
                <div className="finance-categories-grid">
                  {categories.map((category) => (
                    <article key={category.id} className="finance-category-card">
                      <header>
                        <div>
                          <h3>{category.name}</h3>
                          <p>{category.type === 'income' ? 'Receita' : 'Despesa'}{category.isDefault ? ' · padrão' : ''}</p>
                        </div>
                        <span className="finance-dot" style={{ width: 14, height: 14, background: category.color }} />
                      </header>
                      <div className="finance-row-actions">
                        <button type="button" className="finance-btn finance-btn-ghost finance-btn-sm" onClick={() => openCategoryModal(category)}>
                          <FaEdit /> Editar
                        </button>
                        <button
                          type="button"
                          className="finance-btn finance-btn-danger finance-btn-sm"
                          disabled={acting === `del-cat-${category.id}`}
                          onClick={() => removeCategory(category.id)}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {modal && (
          <motion.div
            className="finance-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="finance-modal"
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10 }}
              onClick={(e) => e.stopPropagation()}
            >
              {modal === 'transaction' && (
                <form onSubmit={saveTransaction}>
                  <header>
                    <div>
                      <span className="finance-card-eyebrow">Lançamento</span>
                      <h2>{editingId ? 'Editar movimentação' : 'Novo lançamento'}</h2>
                      <p>Registre receita, despesa ou transferência entre contas.</p>
                    </div>
                    <button type="button" className="finance-modal-close" onClick={closeModal} aria-label="Fechar">
                      <FaTimes />
                    </button>
                  </header>
                  <div className="finance-form-grid">
                    <div className="finance-field is-full">
                      <label>Tipo</label>
                      <div className="finance-type-toggle">
                        {[
                          { value: 'income', label: 'Receita', className: 'is-income' },
                          { value: 'expense', label: 'Despesa', className: 'is-expense' },
                          { value: 'transfer', label: 'Transferência', className: '' },
                        ].map((item) => (
                          <button
                            key={item.value}
                            type="button"
                            className={`${item.className} ${txForm.type === item.value ? 'is-active' : ''}`}
                            onClick={() => setTxForm((c) => ({
                              ...c,
                              type: item.value,
                              categoryId: item.value === 'income'
                                ? (incomeCategories[0]?.id || '')
                                : item.value === 'expense'
                                  ? (expenseCategories[0]?.id || '')
                                  : '',
                              paymentMethod: item.value === 'transfer' ? 'transfer' : c.paymentMethod === 'transfer' ? 'pix' : c.paymentMethod,
                            }))}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="finance-field">
                      <label>Valor</label>
                      <input
                        required
                        type="text"
                        inputMode="decimal"
                        value={txForm.amount}
                        onChange={(e) => setTxForm((c) => ({ ...c, amount: e.target.value }))}
                        placeholder="0,00"
                      />
                    </div>
                    <div className="finance-field">
                      <label>Data</label>
                      <input
                        required
                        type="date"
                        value={txForm.date}
                        onChange={(e) => setTxForm((c) => ({ ...c, date: e.target.value }))}
                      />
                    </div>
                    <div className="finance-field is-full">
                      <label>Descrição</label>
                      <input
                        required
                        type="text"
                        maxLength={120}
                        value={txForm.description}
                        onChange={(e) => setTxForm((c) => ({ ...c, description: e.target.value }))}
                        placeholder="Ex.: Aluguel, cliente X, mercado…"
                      />
                    </div>
                    <div className="finance-field">
                      <label>{txForm.type === 'transfer' ? 'Conta de origem' : 'Conta'}</label>
                      <select
                        required
                        value={txForm.accountId}
                        onChange={(e) => setTxForm((c) => ({ ...c, accountId: e.target.value }))}
                      >
                        <option value="">Selecione</option>
                        {accounts.map((account) => (
                          <option key={account.id} value={account.id}>{account.name}</option>
                        ))}
                      </select>
                    </div>
                    {txForm.type === 'transfer' ? (
                      <div className="finance-field">
                        <label>Conta de destino</label>
                        <select
                          required
                          value={txForm.transferToAccountId}
                          onChange={(e) => setTxForm((c) => ({ ...c, transferToAccountId: e.target.value }))}
                        >
                          <option value="">Selecione</option>
                          {accounts.filter((a) => a.id !== txForm.accountId).map((account) => (
                            <option key={account.id} value={account.id}>{account.name}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="finance-field">
                        <label>Categoria</label>
                        <select
                          required
                          value={txForm.categoryId}
                          onChange={(e) => setTxForm((c) => ({ ...c, categoryId: e.target.value }))}
                        >
                          <option value="">Selecione</option>
                          {categoryOptions.map((category) => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="finance-field">
                      <label>Forma de pagamento</label>
                      <select
                        value={txForm.paymentMethod}
                        onChange={(e) => setTxForm((c) => ({ ...c, paymentMethod: e.target.value }))}
                      >
                        {PAYMENT_METHODS.map((method) => (
                          <option key={method.value} value={method.value}>{method.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="finance-field">
                      <label>Status</label>
                      <select
                        value={txForm.status}
                        onChange={(e) => setTxForm((c) => ({ ...c, status: e.target.value }))}
                      >
                        <option value="confirmed">Confirmado</option>
                        <option value="pending">Pendente</option>
                      </select>
                    </div>
                    <div className="finance-field is-full">
                      <label>Tags (separadas por vírgula)</label>
                      <input
                        type="text"
                        value={txForm.tags}
                        onChange={(e) => setTxForm((c) => ({ ...c, tags: e.target.value }))}
                        placeholder="fix, recorrente, urgente"
                      />
                    </div>
                    <div className="finance-field is-full">
                      <label>Notas</label>
                      <textarea
                        value={txForm.notes}
                        onChange={(e) => setTxForm((c) => ({ ...c, notes: e.target.value }))}
                        placeholder="Detalhes opcionais"
                      />
                    </div>
                  </div>
                  <div className="finance-modal-actions">
                    <button type="button" className="finance-btn finance-btn-ghost" onClick={closeModal}>Cancelar</button>
                    <button type="submit" className="finance-btn finance-btn-primary" disabled={acting === 'tx'}>
                      {acting === 'tx' ? <FaSpinner /> : null}
                      Salvar
                    </button>
                  </div>
                </form>
              )}

              {modal === 'account' && (
                <form onSubmit={saveAccount}>
                  <header>
                    <div>
                      <span className="finance-card-eyebrow">Conta</span>
                      <h2>{editingId ? 'Editar conta' : 'Nova conta'}</h2>
                      <p>Organize saldos por carteira, banco ou cartão.</p>
                    </div>
                    <button type="button" className="finance-modal-close" onClick={closeModal} aria-label="Fechar">
                      <FaTimes />
                    </button>
                  </header>
                  <div className="finance-form-grid">
                    <div className="finance-field is-full">
                      <label>Nome</label>
                      <input required type="text" maxLength={60} value={accountForm.name} onChange={(e) => setAccountForm((c) => ({ ...c, name: e.target.value }))} />
                    </div>
                    <div className="finance-field">
                      <label>Tipo</label>
                      <select value={accountForm.type} onChange={(e) => setAccountForm((c) => ({ ...c, type: e.target.value }))}>
                        {ACCOUNT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="finance-field">
                      <label>Saldo inicial</label>
                      <input type="text" inputMode="decimal" value={accountForm.initialBalance} onChange={(e) => setAccountForm((c) => ({ ...c, initialBalance: e.target.value }))} />
                    </div>
                    <div className="finance-field">
                      <label>Cor</label>
                      <input type="color" value={accountForm.color} onChange={(e) => setAccountForm((c) => ({ ...c, color: e.target.value }))} />
                    </div>
                    <div className="finance-field is-full">
                      <label>Notas</label>
                      <textarea value={accountForm.notes} onChange={(e) => setAccountForm((c) => ({ ...c, notes: e.target.value }))} />
                    </div>
                  </div>
                  <div className="finance-modal-actions">
                    <button type="button" className="finance-btn finance-btn-ghost" onClick={closeModal}>Cancelar</button>
                    <button type="submit" className="finance-btn finance-btn-primary" disabled={acting === 'account'}>Salvar</button>
                  </div>
                </form>
              )}

              {modal === 'category' && (
                <form onSubmit={saveCategory}>
                  <header>
                    <div>
                      <span className="finance-card-eyebrow">Categoria</span>
                      <h2>{editingId ? 'Editar categoria' : 'Nova categoria'}</h2>
                      <p>Classifique receitas e despesas com consistência.</p>
                    </div>
                    <button type="button" className="finance-modal-close" onClick={closeModal} aria-label="Fechar">
                      <FaTimes />
                    </button>
                  </header>
                  <div className="finance-form-grid">
                    <div className="finance-field is-full">
                      <label>Nome</label>
                      <input required type="text" maxLength={48} value={categoryForm.name} onChange={(e) => setCategoryForm((c) => ({ ...c, name: e.target.value }))} />
                    </div>
                    <div className="finance-field">
                      <label>Tipo</label>
                      <select
                        disabled={Boolean(editingId)}
                        value={categoryForm.type}
                        onChange={(e) => setCategoryForm((c) => ({
                          ...c,
                          type: e.target.value,
                          color: e.target.value === 'income' ? '#4dbc8b' : '#d4a35c',
                        }))}
                      >
                        <option value="expense">Despesa</option>
                        <option value="income">Receita</option>
                      </select>
                    </div>
                    <div className="finance-field">
                      <label>Cor</label>
                      <input type="color" value={categoryForm.color} onChange={(e) => setCategoryForm((c) => ({ ...c, color: e.target.value }))} />
                    </div>
                  </div>
                  <div className="finance-modal-actions">
                    <button type="button" className="finance-btn finance-btn-ghost" onClick={closeModal}>Cancelar</button>
                    <button type="submit" className="finance-btn finance-btn-primary" disabled={acting === 'category'}>Salvar</button>
                  </div>
                </form>
              )}

              {modal === 'budget' && (
                <form onSubmit={saveBudget}>
                  <header>
                    <div>
                      <span className="finance-card-eyebrow">Orçamento</span>
                      <h2>{editingId ? 'Ajustar limite' : 'Novo limite mensal'}</h2>
                      <p>Controle o teto de gastos por categoria em {monthLabel(month)}.</p>
                    </div>
                    <button type="button" className="finance-modal-close" onClick={closeModal} aria-label="Fechar">
                      <FaTimes />
                    </button>
                  </header>
                  <div className="finance-form-grid">
                    <div className="finance-field is-full">
                      <label>Categoria de despesa</label>
                      <select required value={budgetForm.categoryId} onChange={(e) => setBudgetForm((c) => ({ ...c, categoryId: e.target.value }))}>
                        <option value="">Selecione</option>
                        {expenseCategories.map((category) => (
                          <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="finance-field">
                      <label>Limite</label>
                      <input required type="text" inputMode="decimal" value={budgetForm.limitAmount} onChange={(e) => setBudgetForm((c) => ({ ...c, limitAmount: e.target.value }))} placeholder="0,00" />
                    </div>
                    <div className="finance-field is-full">
                      <label>Notas</label>
                      <textarea value={budgetForm.notes} onChange={(e) => setBudgetForm((c) => ({ ...c, notes: e.target.value }))} />
                    </div>
                  </div>
                  <div className="finance-modal-actions">
                    <button type="button" className="finance-btn finance-btn-ghost" onClick={closeModal}>Cancelar</button>
                    <button type="submit" className="finance-btn finance-btn-primary" disabled={acting === 'budget'}>Salvar</button>
                  </div>
                </form>
              )}

              {modal === 'goal' && (
                <form onSubmit={saveGoal}>
                  <header>
                    <div>
                      <span className="finance-card-eyebrow">Meta</span>
                      <h2>{editingId ? 'Editar meta' : 'Nova meta'}</h2>
                      <p>Acompanhe progressos com alvo, prazo e status.</p>
                    </div>
                    <button type="button" className="finance-modal-close" onClick={closeModal} aria-label="Fechar">
                      <FaTimes />
                    </button>
                  </header>
                  <div className="finance-form-grid">
                    <div className="finance-field is-full">
                      <label>Nome</label>
                      <input required type="text" maxLength={80} value={goalForm.name} onChange={(e) => setGoalForm((c) => ({ ...c, name: e.target.value }))} />
                    </div>
                    <div className="finance-field">
                      <label>Valor alvo</label>
                      <input required type="text" inputMode="decimal" value={goalForm.targetAmount} onChange={(e) => setGoalForm((c) => ({ ...c, targetAmount: e.target.value }))} />
                    </div>
                    <div className="finance-field">
                      <label>Valor atual</label>
                      <input type="text" inputMode="decimal" value={goalForm.currentAmount} onChange={(e) => setGoalForm((c) => ({ ...c, currentAmount: e.target.value }))} />
                    </div>
                    <div className="finance-field">
                      <label>Prazo</label>
                      <input type="date" value={goalForm.deadline} onChange={(e) => setGoalForm((c) => ({ ...c, deadline: e.target.value }))} />
                    </div>
                    <div className="finance-field">
                      <label>Status</label>
                      <select value={goalForm.status} onChange={(e) => setGoalForm((c) => ({ ...c, status: e.target.value }))}>
                        <option value="active">Ativa</option>
                        <option value="paused">Pausada</option>
                        <option value="completed">Concluída</option>
                      </select>
                    </div>
                    <div className="finance-field">
                      <label>Cor</label>
                      <input type="color" value={goalForm.color} onChange={(e) => setGoalForm((c) => ({ ...c, color: e.target.value }))} />
                    </div>
                    <div className="finance-field is-full">
                      <label>Notas</label>
                      <textarea value={goalForm.notes} onChange={(e) => setGoalForm((c) => ({ ...c, notes: e.target.value }))} />
                    </div>
                  </div>
                  <div className="finance-modal-actions">
                    <button type="button" className="finance-btn finance-btn-ghost" onClick={closeModal}>Cancelar</button>
                    <button type="submit" className="finance-btn finance-btn-primary" disabled={acting === 'goal'}>Salvar</button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
